import React, { useCallback } from "react";
import { useForm } from "react-hook-form-7";
import useNotification from "../../../hooks/useNotification";
import useFormatDate from "../../../hooks/useFormatDate";
import usePDFConversion from "../../../hooks/usePDFConversion";

import useTranslations from "../../../hooks/useTranslations";

import {
  Avatar,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  VStack,
  FormControl,
  FormLabel,
  Text,
  Stack,
  Checkbox,
} from "@chakra-ui/react";
import {
  GetReportDocument,
  ReportFragment,
  UserSummaryFragment,
  useSignReport,
} from "../../../graphql/generated/definitions";

type ReportSignatureModalProps = {
  open: boolean;
  onClose: () => void;
  report: ReportFragment;
  signatories: (UserSummaryFragment & {
    reviewRole: "collaborator" | "referent" | "supervisor";
  })[];
};

type SignatoriesData = {
  signatories: string[];
};

const ReportSignatureModal: React.FC<ReportSignatureModalProps> = ({
  open,
  onClose,
  report,
  signatories,
}) => {
  const t = useTranslations();

  const { register, handleSubmit } = useForm<SignatoriesData>({
    defaultValues: { signatories: signatories.map(({ id }) => id) },
  });

  const [signReport, { loading: signLoading }] = useSignReport();
  const { notifySuccess, notifyError } = useNotification();

  const filledAtFormatted = useFormatDate({
    date: report.filledAt,
    format: "long",
  });
  const { convert: convertReportToPDF, loading: convertReportToPDFLoading } =
    usePDFConversion();
  const onSubmit = useCallback(
    async (data: SignatoriesData) => {
      try {
        const blob = await convertReportToPDF({
          format: "A4",
          url: window.location.href,
          withCredentials: true,
          extraQueryParams: { signatoryIds: data.signatories },
        });

        const reader = new FileReader();

        reader.addEventListener(
          "load",
          async () => {
            await signReport({
              variables: {
                input: {
                  reportId: report.id,
                  signatoryIds: data.signatories,
                  document: reader.result as string,
                  filename: t("reports.pdf.filename", {
                    collaborator: report.review.collaborator.name,
                    filledAt: filledAtFormatted,
                  }) as string,
                },
              },
              refetchQueries: [
                { query: GetReportDocument, variables: { id: report.id } },
              ],
            });

            notifySuccess({
              title: t(`reports.signature.notification.success`) as string,
              description: t(
                `reports.signature.notification.signatureAsked`
              ) as string,
            });
            onClose();
          },
          false
        );

        reader.readAsDataURL(blob);
      } catch (error) {
        notifyError({
          title: t(`userProfile.notification.error`) as string,
        });
      }
    },
    [signReport, report, convertReportToPDF]
  );

  return (
    <Modal
      isOpen={open}
      aria-label="Sign report"
      id={`signModal`}
      onClose={onClose}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("reports.signature.header")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)} id="report-sign-form">
            <FormControl>
              <FormLabel>{t("reports.signature.selectSignatories")}</FormLabel>
              <VStack align="stretch" spacing={4}>
                {signatories.map((signatory) => {
                  return (
                    <Stack
                      as="label"
                      key={signatory.id}
                      id={`signatory-${signatory.id}`}
                      spacing="8"
                      rounded="lg"
                      px="5"
                      py="6"
                      border="1px"
                      borderColor="gray.200"
                      direction="row"
                    >
                      <Checkbox
                        {...register("signatories")}
                        value={signatory.id}
                        id={`signatory-${signatory.id}-checkbox`}
                      />
                      <Stack direction="row" spacing="4">
                        <Avatar
                          src={signatory?.avatar}
                          name={signatory?.name}
                        />
                        <Stack spacing="0">
                          <Text fontSize="sm" fontWeight="semibold">
                            {signatory?.name}
                          </Text>
                          <Text fontSize="sm">
                            {t(
                              `reports.signature.role.${signatory.reviewRole}` as const
                            )}
                          </Text>
                        </Stack>
                      </Stack>
                    </Stack>
                  );
                })}
              </VStack>
            </FormControl>
          </form>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button
              onClick={onClose}
              id={`cancelSignatureButton`}
              disabled={convertReportToPDFLoading || signLoading}
            >
              {t("forms.actions.cancel")}
            </Button>

            <Spacer />

            <Button
              isLoading={convertReportToPDFLoading || signLoading}
              type="submit"
              form="report-sign-form"
              id={`submitSignatureButton`}
              colorScheme="primary"
            >
              {t("forms.actions.confirm")}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(ReportSignatureModal);
